/**
 * Site-configuration data model, reproduced from the Signpost Directus schema
 * (Directus 10.8.3 / MySQL, snapshot taken 2026-08-25).
 *
 * Everything lives in localStorage — see siteconfig/store.ts. Nothing here
 * reaches Directus, Supabase, or any other production system.
 *
 * This is branding and landing-page content — colors, header/footer nav, and
 * the marketing/informational blocks that make up a country's public site.
 * It does NOT include bot/AI config (ai, botsconfig, constitutionalai, kb),
 * social-post mirrors (facebook_posts, instagram_posts), or any Directus
 * system collection — those are out of scope, same as in services/types.ts.
 *
 * FIDELITY NOTES:
 *
 * 1. SITE IS THE ROOT — a deliberate break from Directus.
 *
 *    In production, Country was effectively the root: `siteconfig.country`
 *    was a single FK to countries_n, and Country itself carried the site's
 *    language list, base url, analytics ids and translation settings. That
 *    conflated two unrelated things — "where the services are" and "what this
 *    site is" — and made a site's language list a property of geography.
 *
 *    Here, Site is the top-level entity and owns all site-level config
 *    (identity, locales, analytics, branding, nav, content). Geography is NOT
 *    owned: Site.serviceMap holds free-form FK lists into the SERVICE MAP
 *    store, which owns Country/Region/City. Articles work the same way via
 *    Site.articles. Several sites may therefore reference the same country,
 *    and a country belongs to no config at all — the old
 *    one-country-one-config constraint is gone, along with the store.ts
 *    reassignment logic that enforced it.
 *
 *    Directus's `content` M2M becomes contentIds, a plain id array, matching
 *    the services model's treatment of junction tables. Its `headercontent`
 *    M2M does NOT survive as an id array — see note 1b, which replaces it
 *    with an embedded tree.
 *
 * 1b. HEADER/FOOTER RESHAPE — superseding an earlier version of this note.
 *     The raw Directus admin schema treats headerlinks/footerlinks as flat
 *     collections of rows. But the app's actual RUNTIME shape — the
 *     transformed `app.page.header`/`app.page.footer` object the frontend
 *     renders from, confirmed against a real captured payload — looks
 *     nothing like that:
 *       - header.menu is a RECURSIVE TREE: each item has type
 *         ("menu"|"services"|"link"), a locale-map title, an optional link,
 *         a megamenu flag, and a nested `content: MenuItem[]` of children —
 *         inline, not a flat table of rows joined by id. A "services" item
 *         is a special leaf that links to the Service Map, not a page-content
 *         block.
 *       - footer is a SINGLE object (not several rows): bgColor, textColor,
 *         a translated `text`, and `footerLinks: {title, url}[]` embedded
 *         directly, again not referenced by id.
 *     So this model matches the runtime shape rather than the raw-row shape:
 *     Site.headerMenu: MenuItem[] (recursive) and
 *     Site.footer: SiteFooter (a single embedded object), not separate
 *     HeaderLink/FooterLink entity collections. This is the second departure
 *     from a literal 1:1 Directus-row mirror in this model, alongside the
 *     Site-as-root change in note 1 — both trade "exactly what one Directus
 *     table looks like" for "what actually needs editing," which is the more
 *     useful correspondence for a hand-editable sandbox.
 *
 * 2. PageContent is Directus's real shape, not a cleaned-up one: `pagecontent`
 *    is a single flat table with a `type` discriminator (13 values in
 *    Directus, 12 here — see note 1b on "footer") and ~90
 *    content fields, where each type is SUPPOSED to only use its own fields —
 *    but production rows do not respect that boundary (checked against live
 *    data: every sampled row across all types has fields set outside its
 *    own group, evidently left over from earlier schema iterations). Modeling
 *    this as a strict discriminated union would silently drop that real
 *    content. So PageContent is one flat interface with every field optional,
 *    and PAGECONTENT_FIELD_GROUPS below records which fields are CANONICAL for
 *    each type (i.e. what Directus's own conditional UI shows) without
 *    pretending the others are never populated.
 *
 * 3. Translations: Directus splits pagecontent's translatable fields across 14
 *    sibling tables (pagecontent_translations, _1..._13). Three of them
 *    (_6, _10, _14) exist in the schema but carry no content fields at all —
 *    confirmed empty, not fetched, presumably abandoned mid-migration — so
 *    they contribute nothing here. The other 11 map onto TranslatedPageContent
 *    below. As elsewhere in this app, translations collapse to one
 *    `Record<locale, Translated>` map on the entity rather than staying as
 *    separate joined rows.
 *
 * 4. Directus ids for these collections are auto-increment integers (or a uuid
 *    for siteconfig itself); we use ULIDs, consistent with services/ and cms/,
 *    and keep `oldid` for the legacy id.
 *
 * 5. Directus stamps every row in these collections with user_created,
 *    date_created, user_updated, date_updated (confirmed on siteconfig via
 *    the admin field editor; the same four fields appear on pagecontent too).
 *    Only the two TIMESTAMPS are modelled here, as createdAt/updatedAt. The
 *    two USER columns are deliberately not: this sandbox already records who
 *    changed what, with far more detail, in the change log (see
 *    siteconfig/history.ts, which stores an author name/email/id and the
 *    model id for agent edits, per change rather than only for the latest
 *    one). Duplicating a last-writer stamp onto the row would be a second,
 *    weaker source of truth for the same question.
 */

// ── Site configuration (one per country) ────────────────────────────────────

import { hasTranslationContent } from "../shared/languages"
export interface Site {
  id: string
  /** Public-facing site name, e.g. "Signpost Greece". */
  name?: string
  /** Public site base url. Directus kept this on Country as `site`. */
  baseUrl?: string
  /** Site logo — a URL, or a legacy Directus file id. */
  logo?: string

  /**
   * Locales this site publishes in, and the one everything falls back to.
   * These lived on Country upstream (`locale` / `languages`), which forced a
   * language list to be a property of geography. A site's language list is a
   * property of the SITE, so it lives here.
   */
  defaultLocale?: string
  locales: string[]

  /**
   * Per-site Google Analytics measurement ids. Upstream this was
   * `Country.googletag: { key, type }[]`, read by <Analytics> at page load.
   */
  googleTag: SiteAnalyticsTag[]
  /** Transifex project slug; upstream Country.transifexProject. */
  transifexProject?: string
  /** Use Google Translate instead of Transifex; upstream Country.googletranslate. */
  googleTranslate?: boolean

  /** Plain text, not HTML, despite the multiline input. */
  cookieBanner?: string
  /** Per-locale cookie banner text. */
  cookieBannerTranslations?: Record<string, string>

  // Branding colors — free hex/named values, no enforced palette.
  headerBgColor?: string
  headerColor?: string
  headerColorBg?: string
  headerColorTxt?: string
  pageBgColor?: string
  pageColor?: string
  pageColorBg?: string
  pageColorTxt?: string

  /** Ids of PageContent blocks shown on this site, in render order. */
  contentIds: string[]
  /** Header navigation — a recursive tree. */
  headerMenu: MenuItem[]
  /** Footer — a single embedded object. */
  footer: SiteFooter

  /** What this site draws from the other two backends. */
  serviceMap: SiteServiceMapScope
  articles: SiteArticlesScope

  oldid?: number
  createdAt: number
  updatedAt: number
}

/** One Google Analytics / Tag Manager id. Mirrors upstream Country.googletag. */
export interface SiteAnalyticsTag {
  key: string
  /** e.g. "GA4", "GTM". Free text upstream. */
  type?: string
}

/**
 * Which service-map geography this site surfaces.
 *
 * Free-form by design: the three lists are independent and no hierarchy is
 * enforced, so a site can be a whole country, a single city, or any mix —
 * listing a city does NOT require listing its country. An empty scope means
 * the site surfaces no services rather than all of them, so a new site starts
 * silent instead of accidentally publishing every service in the database.
 *
 * These are FKs into the SERVICE MAP store, which owns the entities
 * themselves. Site only ever references them.
 */
export interface SiteServiceMapScope {
  countryIds: string[]
  regionIds: string[]
  cityIds: string[]
}

/**
 * Which article categories this site surfaces. FKs into the ARTICLES store.
 * `allCategories` publishes the whole knowledge base and makes categoryIds
 * irrelevant — the common case, and it keeps a site from going stale when a
 * new category is added.
 */
export interface SiteArticlesScope {
  allCategories: boolean
  categoryIds: string[]
}

// ── Navigation ───────────────────────────────────────────────────────────────

/**
 * One header nav item. Recursive: a megamenu item's `children` holds its
 * dropdown entries, which are themselves MenuItems (confirmed to be plain
 * links in practice, but the runtime shape doesn't rule out further nesting).
 * Titles are locale maps inline, not a joined translations table — that
 * matches the real payload, where title is already `{locale: text}`.
 */
/**
 * Every nav item type the production header renders.
 *
 * Confirmed against signpost-monorepo lib/components/header.tsx, which
 * branches on "about", "menu", "services", "bot" and "info". An earlier
 * version of this model carried only link/menu/services, which would have
 * silently dropped or mistyped the rest on migration.
 */
export const MENU_ITEM_TYPES = [
  /** A plain nav item with a URL. */
  "link",
  /** A megamenu dropdown; its entries are this item's children. */
  "menu",
  /** Links to the Service Map. */
  "services",
  /** The About entry, which the header pulls out and renders specially. */
  "about",
  /** Opens the bot/assistant. Rendered as its own control, not a link. */
  "bot",
  /** Informational entry, filtered OUT of the main nav rows upstream. */
  "info",
  /** Language switcher. */
  "language",
] as const
export type MenuItemType = (typeof MENU_ITEM_TYPES)[number]

export const MENU_ITEM_TYPE_LABELS: Record<MenuItemType, string> = {
  link: "Link",
  menu: "Dropdown menu",
  services: "Service Map",
  about: "About",
  bot: "Bot",
  info: "Info",
  language: "Language switcher",
}

export interface MenuItem {
  id: string
  type: MenuItemType
  title: Record<string, string>
  /** Present on "link" items; a "menu" item is just a dropdown label with no link of its own. */
  link?: string
  /** True for a "menu" item that should render as a dropdown rather than a plain link. */
  megamenu?: boolean
  /** Nested items, for a megamenu dropdown. */
  children: MenuItem[]
}

/** One footer link — embedded inline, not a separate entity (see FIDELITY NOTE 1b). */
export interface FooterLink {
  title: Record<string, string>
  url?: string
}

/**
 * The footer — a single embedded object per Site, not several rows.
 * Directus's raw `pagecontent` type "footer" is the closest raw-table
 * analogue, but the runtime object the frontend actually renders (confirmed
 * against a live payload) has this flatter shape.
 */
export interface SiteFooter {
  bgColor?: string
  textColor?: string
  text?: Record<string, string>
  footerLinks: FooterLink[]
}

/*
 * The block model lives in ./pageContent.ts. Re-exported here because every
 * caller imports these names from "./types", and the store shape below
 * references PageContent directly.
 */
export * from "./pageContent"
// `export *` re-exports but does not bind locally, and the code below uses
// these directly.
import {
  type PageContent, type PageContentType, type TranslatedPageContent,
  PAGECONTENT_FIELD_GROUPS, PAGECONTENT_TYPE_LABELS,
} from "./pageContent"

// ── Store ────────────────────────────────────────────────────────────────────

export interface SiteConfigStore {
  /** 2: Site replaced SiteConfig as the root. v1 payloads are discarded. */
  version: 2
  defaultLocale: string
  sites: Record<string, Site>
  pageContents: Record<string, PageContent>
}

/**
 * headerMenu and footer are no longer separate entity kinds — they're
 * embedded arrays/objects directly on Site (see FIDELITY NOTE 1b), so
 * they're edited as part of editing a Site rather than as their own
 * rows with their own ids.
 */
export type SiteConfigEntityKind = "site" | "pageContent"

export const SITECONFIG_BUCKET: Record<SiteConfigEntityKind, keyof SiteConfigStore> = {
  site: "sites",
  pageContent: "pageContents",
}

export function emptySiteConfigStore(defaultLocale = "en-US"): SiteConfigStore {
  return {
    version: 2,
    defaultLocale,
    sites: {},
    pageContents: {},
  }
}

/**
 * Locale-aware label for any entity in this store. Unlike services/types.ts's
 * displayName, there is no single "name" field shared across kinds here — a
 * A Site is named by its name and a PageContent block has no name field
 * at all (its identity is `type` plus whichever canonical title field that
 * type uses), so each kind needs its own logic.
 */
export function siteConfigDisplayName(
  kind: SiteConfigEntityKind,
  entity: any,
  locale: string,
  defaultLocale: string
): string {
  if (!entity) return "(untitled)"

  if (kind === "site") {
    return entity.name || "(untitled site)"
  }

  // pageContent: the admin-facing `title` exists precisely to identify a block,
  // so it wins when set.
  if (entity.title) return String(entity.title)

  // Otherwise try the canonical title field for its type, in either the
  // requested locale's translation or the base row, then fall back to the
  // type label so an untitled block is still identifiable in a list.
  const type = entity.type as PageContentType
  const titleField = PAGECONTENT_FIELD_GROUPS[type]?.find(f => String(f).toLowerCase().endsWith("title"))
  if (titleField) {
    const t = entity.translations?.[locale] ?? entity.translations?.[defaultLocale]
    const translated = t?.[titleField as keyof TranslatedPageContent]
    if (translated) return String(translated)
    const base = entity[titleField]
    if (base) return String(base)
  }
  return `(${PAGECONTENT_TYPE_LABELS[type] ?? type} block)`
}

/**
 * Every locale that actually has content somewhere in the store.
 *
 * Site config has no `languages` collection of its own, so before this existed
 * the editor's locale dropdown was fed the Services backend's list. Any locale
 * written here but not configured over there — the common case when the agent
 * translates a page — was invisible in the UI and looked like data loss, even
 * though the translation was on disk the whole time.
 *
 * Translations live in four different carriers, so all four are walked:
 * PageContent.translations, cookieBannerTranslations, and the inline
 * `{locale: text}` maps on menu items (recursively, via children) and on the
 * footer's text and links.
 */
const hasText = (v: unknown) => typeof v === "string" ? v.trim() !== "" : hasTranslationContent(v)

export function localesInSiteConfigStore(store: SiteConfigStore): string[] {
  const found = new Set<string>()
  // Only slots with real text: an empty key left behind by a cleared title isn't a language the site has
  const addMap = (m?: Record<string, unknown> | null) => {
    if (m) for (const [k, v] of Object.entries(m)) if (k && hasText(v)) found.add(k)
  }
  const addMenu = (items?: MenuItem[] | null) => {
    for (const item of items ?? []) {
      addMap(item.title)
      addMenu(item.children)
    }
  }

  for (const site of Object.values(store.sites ?? {})) {
    for (const l of site.locales ?? []) if (l) found.add(l)
    if (site.defaultLocale) found.add(site.defaultLocale)
    addMap(site.cookieBannerTranslations)
    addMenu(site.headerMenu)
    addMap(site.footer?.text)
    for (const link of site.footer?.footerLinks ?? []) addMap(link.title)
  }
  for (const content of Object.values(store.pageContents ?? {})) {
    addMap(content.translations)
  }

  found.delete(store.defaultLocale)
  return [store.defaultLocale, ...[...found].sort()]
}
